import { Layout, Menu, Space, Typography, Dropdown, Avatar, Button } from 'antd'
import {
  ApiOutlined,
  AppstoreOutlined,
  CloudUploadOutlined,
  FireOutlined,
  LogoutOutlined,
  SettingOutlined,
  TeamOutlined,
  FileSearchOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'

import { SupportWidget } from './SupportWidget'

import { TrialBanner } from './TrialBanner'

import { useAuth } from '../auth/AuthContext'
import { useProject } from '../api/hooks'
import { isDemoRoute } from '../demo/demo'

const { Header, Sider, Content } = Layout

/**
 * AppLayout is the shell every authenticated page renders inside.
 *
 * The sidebar only appears once a project is selected: navigation below the
 * project level is meaningless without one, and showing dead links is worse
 * than showing none.
 */
export function AppLayout() {
  const { user, organization, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { projectId } = useParams<{ projectId: string }>()
  const { data: project } = useProject(projectId)

  const demo = isDemoRoute(location.pathname)
  const selectedKey = selectedMenuKey(location.pathname)

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingInline: 24,
        }}
      >
        <Space size="large">
          <Link to={demo ? '/' : '/projects'}>
            <Space>
              <FireOutlined style={{ color: '#fa8c16', fontSize: 20 }} />
              <Typography.Text strong style={{ color: '#fff', fontSize: 16 }}>
                PecGoProf
              </Typography.Text>
            </Space>
          </Link>
          {project && (
            <Typography.Text style={{ color: 'rgba(255,255,255,0.65)' }}>
              {project.name}
            </Typography.Text>
          )}
        </Space>

        {demo ? (
          <Space size="middle">
            <Typography.Text style={{ color: 'rgba(255,255,255,0.65)' }}>
              Sample data, read-only
            </Typography.Text>
            <Button type="primary" onClick={() => navigate('/login?tab=register')}>
              Create your account
            </Button>
          </Space>
        ) : (
        <Dropdown
          menu={{
            items: [
              {
                key: 'organization',
                label: organization?.name ?? '',
                disabled: true,
              },
              { type: 'divider' },
              {
                key: 'members',
                icon: <TeamOutlined />,
                label: 'People',
                onClick: () => navigate('/members'),
              },
              {
                key: 'audit',
                icon: <FileSearchOutlined />,
                label: 'Audit log',
                onClick: () => navigate('/audit'),
              },
              {
                key: 'settings',
                icon: <SettingOutlined />,
                label: 'Settings',
                onClick: () => navigate('/settings'),
              },
              {
                key: 'sign-out',
                icon: <LogoutOutlined />,
                label: 'Sign out',
                onClick: signOut,
              },
            ],
          }}
        >
          <Space style={{ cursor: 'pointer' }}>
            <Avatar size="small" icon={<UserOutlined />} />
            <Typography.Text style={{ color: 'rgba(255,255,255,0.85)' }}>
              {user?.name ?? user?.email}
            </Typography.Text>
          </Space>
        </Dropdown>
        )}
      </Header>

      <Layout>
        {projectId && (
          <Sider width={220} theme="light" breakpoint="lg" collapsedWidth={0}>
            <Menu
              mode="inline"
              selectedKeys={[selectedKey]}
              style={{ height: '100%', borderInlineEnd: 0 }}
              items={[
                {
                  key: 'overview',
                  icon: <AppstoreOutlined />,
                  label: <Link to={`/projects/${projectId}`}>Overview</Link>,
                },
                {
                  key: 'profiles',
                  icon: <FireOutlined />,
                  label: <Link to={`/projects/${projectId}/profiles`}>Profiles</Link>,
                },
                {
                  key: 'connect',
                  icon: <ApiOutlined />,
                  label: <Link to={`/projects/${projectId}/connect`}>Connect a service</Link>,
                },
                ...(demo
                  ? []
                  : [
                      {
                        key: 'upload',
                        icon: <CloudUploadOutlined />,
                        label: <Link to={`/projects/${projectId}/upload`}>Upload a profile</Link>,
                      },
                      {
                        key: 'settings',
                        icon: <SettingOutlined />,
                        label: <Link to={`/projects/${projectId}/settings`}>Project settings</Link>,
                      },
                    ]),
              ]}
            />
          </Sider>
        )}

        <Content style={{ padding: 24, background: '#f5f5f5' }}>
          {/* Above the page rather than inside one, because the trial applies
              to the account and not to whatever the reader happens to be
              looking at. The demo is nobody's account, so it says nothing. */}
          {!demo && <TrialBanner />}
          <Outlet />
        </Content>
      </Layout>

      {/* Only for somebody signed in: a question without an account behind it
          has no company, no plan and no thread to answer into. */}
      {user ? <SupportWidget /> : null}
    </Layout>
  )
}

function selectedMenuKey(pathname: string): string {
  if (pathname.includes('/profiles') || pathname.includes('/services')) return 'profiles'
  if (pathname.includes('/connect')) return 'connect'
  if (pathname.includes('/upload')) return 'upload'
  if (pathname.includes('/settings')) return 'settings'
  return 'overview'
}
